import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  contractApi,
} from "../../../api/packageApi";
import {
  createContractTemplate,
  DEFAULT_CONTRACT_TEMPLATES,
} from "../../client/StoreRequest/contractTemplates";
import "./ContractLibrary.css";

const ContractLibrary = () => {
  const [contracts, setContracts] = useState(DEFAULT_CONTRACT_TEMPLATES);
  const [logos, setLogos] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState("online-store");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const selectedContract = useMemo(
    () =>
      contracts.find((contract) => contract.id === selectedContractId) ||
      contracts[0],
    [contracts, selectedContractId],
  );

  useEffect(() => {
    const loadLibrary = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await contractApi.getAdminLibrary();
        const savedContracts = response.data?.contracts || [];
        setContracts(
          savedContracts.length > 0
            ? savedContracts
            : DEFAULT_CONTRACT_TEMPLATES,
        );
        setLogos(response.data?.logos || []);
        setSelectedContractId("online-store");
      } catch (loadError) {
        console.error("Error loading contract library:", loadError);
        setError(
          loadError.response?.data?.message ||
            "Could not load the contract library. Confirm the contract tables are set up.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadLibrary();
  }, []);

  const updateSelectedContract = (updates) => {
    if (!selectedContract) return;
    setContracts((current) =>
      current.map((contract) =>
        contract.id === selectedContract.id
          ? { ...contract, ...updates }
          : contract,
      ),
    );
  };

  const addContract = () => {
    const newContract = createContractTemplate(contracts.length + 1);
    setContracts((current) => [...current, { ...newContract, logoId: null }]);
    setSelectedContractId(newContract.id);
    setError("");
  };

  const deleteSelectedContract = () => {
    if (
      !selectedContract ||
      ["online-store", "website"].includes(selectedContract.id)
    ) {
      return;
    }
    setContracts((current) =>
      current.filter((contract) => contract.id !== selectedContract.id),
    );
    setSelectedContractId("online-store");
  };

  const uploadLogo = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setError("Upload a PNG, JPG, or WebP logo.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Logo file must be 2 MB or smaller.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      if (typeof reader.result !== "string") {
        setError("Could not read the selected logo file.");
        return;
      }
      try {
        setUploading(true);
        setError("");
        const response = await contractApi.uploadLogo(
          file.name.replace(/\.[^.]+$/, ""),
          reader.result,
        );
        setLogos((current) => [...current, response.data]);
        toast.success("Logo uploaded. Select it from a contract’s logo list.");
      } catch (uploadError) {
        setError(
          uploadError.response?.data?.message || "Could not upload the logo.",
        );
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => setError("Could not read the selected logo file.");
    reader.readAsDataURL(file);
  };

  const deleteLogo = async (logo) => {
    try {
      setError("");
      await contractApi.deleteLogo(logo.id);
      setLogos((current) => current.filter((item) => item.id !== logo.id));
      toast.success(`Removed logo "${logo.name}".`);
    } catch (deleteError) {
      setError(
        deleteError.response?.data?.message || "Could not delete this logo.",
      );
    }
  };

  const saveLibrary = async () => {
    try {
      setSaving(true);
      setError("");
      await contractApi.saveTemplates(contracts);
      toast.success("Contract library saved successfully.");
    } catch (saveError) {
      setError(
        saveError.response?.data?.message ||
          "Could not save the contract library.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="contract-library-page">
        <div className="contract-library-loading">
          <span className="contract-library-spinner" />
          Loading contracts and logos...
        </div>
      </main>
    );
  }

  return (
    <main className="contract-library-page">
      <header className="contract-library-header">
        <div>
          <span className="contract-library-eyebrow">ADMINISTRATION</span>
          <h1>Contracts &amp; Branding</h1>
          <p>
            Manage reusable service agreements and the logos available to place
            on each contract and its invoice.
          </p>
        </div>
        <button
          type="button"
          className="contract-library-primary"
          onClick={addContract}
        >
          <i className="fas fa-plus" /> New service contract
        </button>
      </header>

      {error && <div className="contract-library-alert error">{error}</div>}
      <section className="contract-logo-library">
        <div className="contract-logo-library-copy">
          <div className="contract-logo-library-icon">
            <i className="fas fa-image" />
          </div>
          <div>
            <h2>Logo library</h2>
            <p>Upload a logo once, then link it to one or more contracts.</p>
          </div>
        </div>
        <label className="contract-library-upload">
          <i className="fas fa-upload" />
          {uploading ? "Uploading..." : "Upload logo"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={uploading}
            onChange={uploadLogo}
          />
        </label>
      </section>

      {logos.length > 0 ? (
        <div className="contract-logo-grid">
          {logos.map((logo) => (
            <article className="contract-logo-item" key={logo.id}>
              <img src={logo.dataUri} alt={logo.name} />
              <span title={logo.name}>{logo.name}</span>
              <button
                type="button"
                aria-label={`Delete ${logo.name}`}
                title="Delete logo"
                onClick={() => deleteLogo(logo)}
              >
                <i className="fas fa-trash-alt" />
              </button>
            </article>
          ))}
        </div>
      ) : (
        <div className="contract-library-empty-logo">
          No logos uploaded yet. Upload one above, then assign it to a contract.
        </div>
      )}

      <section className="contract-library-workspace">
        <aside className="contract-library-list">
          <div className="contract-library-list-heading">
            <div>
              <h2>Contract library</h2>
              <p>{contracts.length} saved templates</p>
            </div>
            <i className="fas fa-file-signature" />
          </div>
          {contracts.map((contract) => (
            <button
              type="button"
              key={contract.id}
              className={`contract-library-list-item ${
                selectedContract?.id === contract.id ? "active" : ""
              }`}
              onClick={() => setSelectedContractId(contract.id)}
            >
              <span className="contract-list-icon">
                <i className="fas fa-file-alt" />
              </span>
              <span className="contract-library-list-label">
                <strong>{contract.name}</strong>
                <small>
                  {contract.id === "online-store"
                    ? "Used during store onboarding"
                    : contract.id === "website"
                      ? "Website services"
                      : "Custom service"}
                </small>
              </span>
              {contract.logoId && (
                <span className="contract-linked-indicator" title="Logo linked">
                  <i className="fas fa-image" />
                </span>
              )}
            </button>
          ))}
        </aside>

        {selectedContract && (
          <section className="contract-library-editor">
            <div className="contract-library-editor-heading">
              <div>
                <span className="contract-library-eyebrow">
                  {selectedContract.id === "online-store"
                    ? "CLIENT ONBOARDING"
                    : "SERVICE TEMPLATE"}
                </span>
                <h2>{selectedContract.name}</h2>
                <p>
                  Edit the contract details and select a logo reference for the
                  client document.
                </p>
              </div>
              {!["online-store", "website"].includes(selectedContract.id) && (
                <button
                  type="button"
                  className="contract-library-danger"
                  onClick={deleteSelectedContract}
                >
                  Delete contract
                </button>
              )}
            </div>

            <div className="contract-library-fields">
              <label>
                Contract name
                <input
                  value={selectedContract.name}
                  maxLength={120}
                  onChange={(event) =>
                    updateSelectedContract({ name: event.target.value })
                  }
                />
              </label>
              <label>
                Document title
                <input
                  value={selectedContract.title}
                  maxLength={200}
                  onChange={(event) =>
                    updateSelectedContract({ title: event.target.value })
                  }
                />
              </label>
              <label>
                Logo reference
                <select
                  value={selectedContract.logoId || ""}
                  onChange={(event) =>
                    updateSelectedContract({
                      logoId: event.target.value || null,
                    })
                  }
                >
                  <option value="">No logo</option>
                  {logos.map((logo) => (
                    <option value={logo.id} key={logo.id}>
                      {logo.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="contract-library-content-field">
              Contract content
              <textarea
                value={selectedContract.content}
                rows={20}
                spellCheck
                onChange={(event) =>
                  updateSelectedContract({ content: event.target.value })
                }
              />
            </label>
            <p className="contract-library-token-note">
              Available merge fields: {"{{AGREEMENT_DATE}}"},{" "}
              {"{{AGREEMENT_NUMBER}}"}, {"{{BUSINESS_NAME}}"},{" "}
              {"{{CONTACT_NAME}}"}, {"{{PHONE}}"}, {"{{EMAIL}}"},{" "}
              {"{{PACKAGE_NAME}}"}, {"{{PRODUCT_LIMIT}}"},{" "}
              {"{{MONTHLY_FEE}}"}, {"{{SETUP_FEE}}"}, {"{{DOMAIN_FEE}}"},{" "}
              {"{{UPFRONT_DUE}}"}, {"{{DOMAIN_TERMS}}"},{" "}
              {"{{TRIAL_TERMS}}"}.
            </p>
            <footer className="contract-library-editor-footer">
              <span>
                <i className="fas fa-database" /> Stored in the contract
                management tables
              </span>
              <button
                type="button"
                className="contract-library-primary"
                onClick={saveLibrary}
                disabled={saving}
              >
                <i className="fas fa-save" />
                {saving ? "Saving..." : "Save contract library"}
              </button>
            </footer>
          </section>
        )}
      </section>
    </main>
  );
};

export default ContractLibrary;
